import { useMemo } from "react";

import { LineChart, Activity } from "lucide-react";

import { AreaChartCard, LineChartCard } from "./Charts";
import { preencherMeses } from "../../lib/faixa";
import { formatPeriodo } from "../../lib/format";
import { Card, CardTitle, Skeleton, EstadoVazio } from "../ui/primitives";

/** Bloco de evolução temporal (nota média + engajamento/conclusão), em dois
 * cards lado a lado. Reutilizável em qualquer escopo (escola, região, rede):
 * recebe a série mensal crua e cuida do preenchimento de meses, do rótulo do
 * eixo e dos estados de carregamento/vazio. */
export function EvolucaoTemporal({
  serie,
  loading = false,
  vazioNotaHint = "Nenhuma avaliação registrada nos meses selecionados.",
  vazioEngHint = "Nenhum score registrado no período."
}) {
  // Preenche meses faltantes → eixo vira linha do tempo contínua (nota e
  // engajamento vêm de aggs distintos, com conjuntos de meses diferentes;
  // sem preencher, os meses só de um deles somem do eixo do outro).
  const pts = useMemo(
    () =>
      preencherMeses(serie ?? []).map(p => ({
        ...p,
        mes_fmt: formatPeriodo(`${p.mes}-01`, "mes")
      })),
    [serie]
  );
  const temEngajamento = useMemo(
    () => pts.some(p => p.engajamento != null),
    [pts]
  );
  const temConclusao = useMemo(() => pts.some(p => p.conclusao != null), [pts]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardTitle meta="Média das avaliações por mês">Nota média</CardTitle>
        {loading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : pts.length ? (
          <AreaChartCard
            data={pts}
            xKey="mes_fmt"
            series={[
              {
                key: "nota_media",
                label: "Nota média",
                color: "var(--color-data)"
              }
            ]}
            yDomain={[0, 10]}
          />
        ) : (
          <EstadoVazio
            icon={<LineChart size={20} />}
            titulo="Sem dados no período"
            hint={vazioNotaHint}
          />
        )}
      </Card>
      <Card>
        <CardTitle
          meta={
            temConclusao
              ? "Score médio e conclusão por mês"
              : "Score de engajamento médio por mês"
          }
        >
          {temConclusao ? "Engajamento e conclusão" : "Engajamento"}
        </CardTitle>
        {loading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : temEngajamento ? (
          <LineChartCard
            data={pts}
            xKey="mes_fmt"
            /* Este par era violeta (chart-6) + azul (chart-2) e era o pior
               defeito de cor do painel: ΔE 5,7 em protanopia e 13,5 em visão
               normal — duas linhas praticamente da mesma cor, sobrepostas no
               mesmo gráfico. Azul + âmbar dá ΔE 32,3 e 38,3. Herdado do
               projeto de origem, que tinha o mesmo problema. */
            series={[
              {
                key: "engajamento",
                label: "Engajamento",
                color: "var(--color-chart-3)"
              },
              ...(temConclusao
                ? [
                    {
                      key: "conclusao",
                      label: "Conclusão",
                      color: "var(--color-chart-2)"
                    }
                  ]
                : [])
            ]}
            yDomain={[0, 100]}
            yTickFormatter={v => `${v}%`}
          />
        ) : (
          <EstadoVazio
            icon={<Activity size={20} />}
            titulo="Sem dados de engajamento"
            hint={vazioEngHint}
          />
        )}
      </Card>
    </div>
  );
}
